import React from "react";
import { useHoverState } from "../hooks";

interface HoverableProps {
  /** If the child of this component is a React Element then will add hover props */
  children?: React.ReactNode | ((isHovered: boolean) => React.ReactNode);
  /** If true wraps the child with a `div` node that detects hover instead */
  useWrapper?: boolean;
  onHoverIn?: () => void;
  onHoverOut?: () => void;
  style?: React.CSSProperties;
}

export function Hoverable(props: HoverableProps) {
  const {
    children,
    onHoverIn = () => {},
    onHoverOut = () => {},
    useWrapper = false,
    style,
  } = props;
  const [isHovered, setIsHovered] = React.useState(false);
  const isHoverEnabled = useHoverState();

  const handleMouseEnter = React.useCallback(() => {
    if (isHoverEnabled && !isHovered) {
      if (onHoverIn) onHoverIn();
      setIsHovered(true);
    }
  }, [isHoverEnabled, isHovered, onHoverIn]);

  const handleMouseLeave = React.useCallback(() => {
    if (isHovered) {
      if (onHoverOut) onHoverOut();
      setIsHovered(false);
    }
  }, [isHovered, onHoverOut]);

  const child = typeof children === "function" ? children(isHovered) : children;

  if (useWrapper) {
    return (
      <div
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        style={style}
      >
        {child}
      </div>
    );
  }

  return React.cloneElement(React.Children.only(child), {
    onMouseEnter: handleMouseEnter,
    onMouseLeave: handleMouseLeave,
  });
}
